import { useMemo, useState } from 'react';
import {
  VOICE_SOURCE_HINTS,
  VOICE_SOURCE_LABELS,
  VOICE_STATUS_MESSAGES,
  type DisplayState,
  type EventSettings,
  type VoiceSource,
} from '@bingo/shared';
import { currentNativeVoice, unlockFromGesture, useVoiceState } from '../../lib/voiceStore.js';
import { speak } from '../../lib/speech.js';

/**
 * Ajustes de narração — o modal "Ajustes" do protótipo, seção de voz.
 *
 * Tudo aqui é salvo em `settingsJson` do evento e chega ao telão pelo
 * WebSocket na hora: trocar a voz ou desligar a narração tem de valer na
 * próxima bola, não no próximo recarregamento.
 *
 * O detalhe que importa: a **lista de vozes é a do aparelho que vai falar**.
 * Com fonte Telão, são as vozes que a TV reportou; com fonte Celular, as deste
 * aparelho. Mostrar as vozes do celular e mandar a TV usá-las resultaria em
 * silêncio — a TV não tem aquela voz.
 */

const RATES = [
  { value: 0.8, label: 'Lenta' },
  { value: 0.95, label: 'Normal' },
  { value: 1.1, label: 'Rápida' },
];

export function NarrationPanel({
  settings,
  displays,
  busy,
  onClose,
  onChange,
  onTestOnDisplay,
}: {
  settings: EventSettings;
  displays: DisplayState[];
  busy: boolean;
  onClose: () => void;
  onChange: (patch: Partial<EventSettings>) => void;
  onTestOnDisplay: (voiceURI: string | null) => void;
}) {
  const phoneVoice = useVoiceState();
  const [testando, setTestando] = useState(false);

  const display = displays[0] ?? null;

  /** Vozes do aparelho que vai falar — e só as em português. */
  const voices = useMemo(() => {
    const todas = settings.voiceSource === 'PHONE' ? phoneVoice.voices : (display?.voices ?? []);
    const portuguesas = todas.filter((voice) => /^pt/i.test(voice.lang));
    return portuguesas.length > 0 ? portuguesas : todas;
  }, [settings.voiceSource, phoneVoice.voices, display?.voices]);

  const testar = () => {
    setTestando(true);
    window.setTimeout(() => setTestando(false), 1500);

    if (settings.voiceSource === 'PHONE') {
      // Precisa ser no gesto: o navegador do celular também bloqueia áudio.
      if (!phoneVoice.unlocked) unlockFromGesture();
      speak(VOICE_TEST_TEXT_LOCAL, { voice: currentNativeVoice(), rate: settings.rate });
      return;
    }

    onTestOnDisplay(settings.voice || null);
  };

  return (
    <div className="overlay">
      <form
        className="modal"
        onSubmit={(event) => {
          event.preventDefault();
          onClose();
        }}
      >
        <h2>Narração</h2>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.narration}
            onChange={(event) => onChange({ narration: event.target.checked })}
          />
          Narrar a bola sorteada
        </label>
        <p className="hint-text" style={{ marginTop: -8 }}>
          Desligado = locutor ao vivo.
        </p>

        <fieldset className="fs">
          <legend>Quem fala</legend>

          <div className="seg">
            {(['SYNTHESIS', 'PHONE', 'MP3'] as VoiceSource[]).map((source) => (
              <label key={source}>
                <input
                  type="radio"
                  name="voiceSource"
                  checked={settings.voiceSource === source}
                  disabled={source === 'MP3'}
                  onChange={() => {
                    // Escolher "Celular" JÁ é o gesto que o navegador exige
                    // para liberar o áudio deste aparelho. Sem isto, o
                    // operador escolhia Celular e ninguém falava: nem a TV
                    // (que cedeu a vez) nem o celular (que seguia travado).
                    if (source === 'PHONE' && !phoneVoice.unlocked) unlockFromGesture();
                    onChange({ voiceSource: source });
                  }}
                />
                <span>
                  {source === 'SYNTHESIS' ? 'Telão' : source === 'PHONE' ? 'Celular' : 'Gravado'}
                  <small>{source === 'MP3' ? 'em breve' : VOICE_SOURCE_LABELS[source]}</small>
                </span>
              </label>
            ))}
          </div>

          <p className="hint-text">{VOICE_SOURCE_HINTS[settings.voiceSource]}</p>

          {/* O estado do telão em texto claro: cada situação pede uma ação
              diferente, e quem opera não vai levantar para ir até a TV. */}
          {display ? (
            <p className={display.voiceStatus === 'READY' ? 'hint-text' : 'error'}>
              {VOICE_STATUS_MESSAGES[display.voiceStatus]}
            </p>
          ) : (
            <p className="hint-text">Nenhum telão conectado.</p>
          )}
        </fieldset>

        <label>
          Voz
          <select
            value={settings.voice}
            onChange={(event) => onChange({ voice: event.target.value })}
            disabled={settings.voiceSource === 'MP3'}
          >
            <option value="">
              {voices.length > 0 ? 'Primeira voz em português' : 'Nenhuma voz disponível'}
            </option>
            {voices.map((voice) => (
              <option key={voice.voiceURI} value={voice.voiceURI}>
                {voice.name} ({voice.lang})
              </option>
            ))}
          </select>
        </label>

        <div className="row2">
          <label>
            Velocidade
            <select
              value={String(settings.rate)}
              onChange={(event) => onChange({ rate: Number(event.target.value) })}
            >
              {RATES.map((rate) => (
                <option key={rate.value} value={rate.value}>
                  {rate.label}
                </option>
              ))}
            </select>
          </label>

          <label className="check" style={{ alignSelf: 'end' }}>
            <input
              type="checkbox"
              checked={settings.digits}
              onChange={(event) => onChange({ digits: event.target.checked })}
            />
            Soletrar os algarismos
          </label>
        </div>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.sfx}
            onChange={(event) => onChange({ sfx: event.target.checked })}
          />
          Efeitos sonoros (suspense, revelação, fanfarra)
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.vibration}
            onChange={(event) => onChange({ vibration: event.target.checked })}
          />
          <span>
            Vibrar neste celular
            <small>Na bola revelada e no resultado da conferência.</small>
          </span>
        </label>

        <div className="actions" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="btn" onClick={testar} disabled={busy || testando}>
            {testando ? 'Falando…' : '🔊 Testar voz'}
          </button>
          <button type="submit" className="btn primary">
            Pronto
          </button>
        </div>
      </form>
    </div>
  );
}

/** Mesmo texto do teste no servidor, para o celular falar sem round-trip. */
const VOICE_TEST_TEXT_LOCAL = 'Gê... 53. 5, 3.';
